import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { type FC, useEffect, useState } from 'react';

import VisionReplayOptions, { type ReplayOptions } from './VisionReplayOptions.js';
import VisionReplayScrubber from './VisionReplayScrubber.js';
import {
  type LiveVisionStage,
  type ReplaySource,
  useCamera,
  useUpdateCameraVisionSettings,
  useVisionReplay,
} from '../../api/index.js';
import { type CameraVisionSettings, DEFAULT_CAMERA_VISION_SETTINGS } from '../../models/index.js';
import VisionStageReadout from '../Camera/VisionStageReadout.js';
import VisionStageToggle from '../Camera/VisionStageToggle.js';

export interface ReplayTarget {
  source: ReplaySource;
  cameraId: string;
  cameraName: string;
}

interface Props {
  target: ReplayTarget | null;
  onClose: () => void;
}

const VisionReplayDialog: FC<Props> = ({ target, onClose }) => {
  const { data: camera, isFetched } = useCamera(target?.cameraId ?? '', { enabled: Boolean(target) });

  return (
    <Dialog open={Boolean(target)} onClose={onClose} maxWidth={'lg'} fullWidth>
      {target && isFetched ? (
        <VisionReplaySession
          key={`${target.source.kind}-${target.source.id}`}
          target={target}
          savedSettings={camera?.visionSettings ?? DEFAULT_CAMERA_VISION_SETTINGS}
          onClose={onClose}
        />
      ) : (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
          <CircularProgress />
        </Box>
      )}
    </Dialog>
  );
};

interface SessionProps {
  target: ReplayTarget;
  savedSettings: CameraVisionSettings;
  onClose: () => void;
}

const VisionReplaySession: FC<SessionProps> = ({ target, savedSettings, onClose }) => {
  const [initialOptions] = useState<ReplayOptions>(() => ({ sampleFps: 2, tracking: true, settings: savedSettings }));
  const [options, setOptions] = useState(initialOptions);
  const [stage, setStage] = useState<LiveVisionStage>('result');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const save = useUpdateCameraVisionSettings(target.cameraId);
  const { status, frames, error, start } = useVisionReplay(target.source);

  useEffect(() => start(initialOptions), [start, initialOptions]);

  const selected = frames[Math.min(selectedIndex, frames.length - 1)];
  const view = selected?.stages[stage];

  const rerun = (next: ReplayOptions) => {
    setOptions(next);
    setSelectedIndex(0);
    start(next);
  };

  return (
    <>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant={'subtitle1'}>Vision replay</Typography>
          <Typography variant={'caption'} color={'text.secondary'} noWrap>
            {target.cameraName} · {frames.length} frame{frames.length === 1 ? '' : 's'}
            {status === 'running' ? ' · processing…' : ''}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} edge={'end'} size={'small'} aria-label={'Close'}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {error && <Alert severity={'error'} sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' } }}>
          <Stack spacing={1.5}>
            <Box sx={{ bgcolor: 'grey.900', borderRadius: 0.5, aspectRatio: '16 / 9', display: 'grid', placeItems: 'center' }}>
              {view ? (
                <Box
                  component={'img'}
                  src={`data:image/jpeg;base64,${view.image}`}
                  alt={`${stage} stage`}
                  sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                status === 'running' && <CircularProgress />
              )}
            </Box>
            <VisionReplayScrubber
              frames={frames}
              selectedIndex={selectedIndex}
              sampleFps={options.sampleFps}
              onSelect={setSelectedIndex}
            />
          </Stack>

          <Stack spacing={2}>
            <VisionStageToggle value={stage} onChange={setStage} />
            <VisionReplayOptions
              value={options}
              savedSettings={savedSettings}
              running={status === 'running'}
              saving={save.isLoading}
              onRun={rerun}
              onSave={(settings) => save.mutate(settings)}
            />
            <VisionStageReadout frame={view && selected ? { ...view, frameIndex: selected.frameIndex } : null} />
          </Stack>
        </Box>
      </DialogContent>
    </>
  );
};

export default VisionReplayDialog;
